import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './IntroPage.module.css';
import logo from '../../assets/pic/TokPlan_New.png'; // Updated logo
import { BsCalendarX, BsLightningCharge, BsCheckCircleFill, BsDownload } from 'react-icons/bs';
import TopBanner from '../../components/Banner/TopBanner.jsx';

const IntroPage = () => {
    const navigate = useNavigate();
    const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setDeferredPrompt(null);
            }
        }
    };

    const handleStart = () => {
        localStorage.setItem('hasSeenIntro', 'true');
        navigate('/start'); // Go to AuthChoice
    };

    const slideVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
    };

    const bubbleVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 200, damping: 20 } }
    };

    return (
        <div className={styles.container}>
            <div className={styles.topBanner}>
                <TopBanner />
            </div>

            {/* Section 1: Problem */}
            <section className={`${styles.section} ${styles.bgPlain}`}>
                <div className={`${styles.floatingShape} ${styles.shape1}`} />
                <motion.div
                    className={styles.contentWrapper}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.5 }}
                    variants={slideVariants}
                >
                    <div className={styles.iconContainer}>
                        <BsCalendarX className={styles.iconLarge} style={{ color: '#fa5252' }} />
                    </div>
                    <h1 className={styles.mainTitle}>
                        여행 계획,<br />
                        여전히 <span className={`${styles.textGradient} ${styles.boldHighlight}`} style={{ color: '#fa5252', textShadow: '0 0 20px rgba(250, 82, 82, 0.4)' }}>엑셀</span>로 하시나요?
                    </h1>
                    <p className={styles.subTitle}>
                        복잡한 동선 체크, 맛집 검색, 예산 정리...<br />
                        떠나기도 전에 지쳐버린 경험.
                    </p>
                </motion.div>
            </section>

            {/* Section 2: Solution */}
            <section className={`${styles.section} ${styles.bgPlain}`}>
                <div className={`${styles.floatingShape} ${styles.shape2}`} />
                <motion.div
                    className={styles.contentWrapper}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.5 }}
                    variants={slideVariants}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    <h1 className={styles.mainTitle}>
                        친구들과 가고 싶은 곳,<br />
                        <span className={styles.textGradient} style={{ color: '#22b8cf', textShadow: '0 0 20px rgba(34, 184, 207, 0.4)' }}>채팅</span>에 남기기만 하세요.
                    </h1>
                    <div className={styles.bubbleContainer}>
                        <motion.div className={styles.chatBubble} variants={bubbleVariants}>"우리 이번에 성수 갔다가 뚝섬이랑 서울숲 가자!"</motion.div>
                        <motion.div className={`${styles.chatBubble} ${styles.chatBubbleRight}`} variants={bubbleVariants}>"네! 최적의 이동 경로로 계획을 완성했습니다."</motion.div>
                    </div>
                </motion.div>
            </section>

            {/* Section 3: Feature */}
            <section className={`${styles.section} ${styles.bgPlain}`}>
                <div className={`${styles.floatingShape} ${styles.shape3}`} />
                <motion.div
                    className={styles.contentWrapper}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.5 }}
                    variants={slideVariants}
                >
                    <div className={styles.iconContainer}>
                        <BsLightningCharge className={styles.iconLarge} style={{ color: '#fcc419' }} />
                    </div>
                    <h1 className={styles.mainTitle}>
                        대화 속 장소들을 모아<br />
                        <span className={`${styles.textGradient} ${styles.boldHighlight}`} style={{ color: '#fcc419', textShadow: '0 0 20px rgba(252, 196, 25, 0.4)' }}>실시간 최적 경로</span>로
                    </h1>
                    <p className={styles.subTitle}>
                        친구들과 나눈 대화만으로<br />
                        가장 완벽한 여행 동선을 설계합니다.
                    </p>
                </motion.div>
            </section>

            {/* Section 4: Result */}
            <section className={`${styles.section} ${styles.bgPlain}`}>
                <motion.div
                    className={styles.contentWrapper}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.5 }}
                    variants={slideVariants}
                >
                    <div className={styles.iconContainer}>
                        <BsCheckCircleFill className={styles.iconLarge} style={{ color: '#51cf66' }} />
                    </div>
                    <h1 className={styles.mainTitle}>
                        계획은 <span className={`${styles.textGradient} ${styles.boldHighlight}`} style={{ color: '#51cf66', textShadow: '0 0 20px rgba(81, 207, 102, 0.4)' }}>TokPlan</span>에게,<br />
                        당신은 설렘만 챙기세요.
                    </h1>
                    <p className={styles.subTitle}>
                        완벽한 여행의 시작,<br />지금 바로 경험해보세요.
                    </p>
                </motion.div>
            </section>

            {/* Section 5: Closing */}
            <section className={`${styles.section} ${styles.bgGradient}`}>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.5 }}
                    variants={slideVariants}
                >
                    <img src={logo} alt="TokPlan" className={styles.logo} />

                    <div style={{ marginTop: '20px', marginBottom: '40px' }}>
                        <p style={{ opacity: 0.8, fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>Created by</p>
                        <h2 style={{ fontSize: '28px', fontWeight: 900, margin: 0 }}>TEAM GANGSTER</h2>
                    </div>

                    <motion.button
                        className={styles.startButton}
                        onClick={handleStart}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        지금 시작하기
                    </motion.button>

                    <motion.button
                        className={styles.installButton}
                        onClick={deferredPrompt ? handleInstallClick : () => alert('이미 앱이 설치되었거나 지원하지 않는 브라우저입니다.')}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        style={{ opacity: deferredPrompt ? 1 : 0.6, cursor: 'pointer' }}
                    >
                        <BsDownload style={{ marginRight: '8px' }} /> 앱으로 설치하기
                    </motion.button>
                </motion.div>
            </section>
        </div>
    );
};

export default IntroPage;
